/**
 * Hybrid Page V2 View
 * 
 **/

import Button, { SECONDARY_THEME, TERTIARY_THEME } from 'components/control/button';
import SelectField from 'components/control/select-field';
import { PaginationMode } from 'components/control/pagination-nav';
import { LocalizedInternalLinks } from 'components/home-page/internal-links';
import FilterIcon from 'components/icon/filter-icon';
import IconButton from 'components/atomic/molecules/icon-button';
import { FilterIcon as AtomicFilterIcon } from 'components/atomic/atoms/icons';
import MobileListingFilterBarV2 from 'ui/mobile-filter-bar-v2/mobile-filter-bar-v2';
import MapIcon from 'components/icon/map-icon';
import ListViewIcon from 'components/icon/list-view-icon';
import { getLocalizedSortOptions } from 'utils/localized-select-options';
import { formatNumberWithSuffix } from 'utils/format-number-with-suffix';
import ListingsGrid from 'components/listings-grid';
import { SurroundingCities } from 'components/related-searches';
import { headerIds, searchFilterIds, testIds } from 'constants/test-constants';
import {
  // GTM_CLICK_HYBRID_PAGE_DESKTOP_EXPAND_MAP,
  // GTM_CLICK_HYBRID_PAGE_DESKTOP_HIDE_MAP,
  // GTM_CLICK_HYBRID_PAGE_DESKTOP_RESTORE_MAP,
  // GTM_CLICK_HYBRID_PAGE_DESKTOP_SHOW_LIST,
  GTM_CLICK_HYBRID_PAGE_INTEL_OPEN,
  GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE,
  GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_AFTER_JIGGLE,
  GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_BEFORE_JIGGLE,
} from 'constants/events';
import { AnalyticsController } from 'analytics/analytics-controller';
import { useExperiment } from '@growthbook/growthbook-react';
import { AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS, Filter, Sort } from 'contexts/preferences/listing-params/types';
import { useIsMobile } from 'hooks/use-size-class';
import { useScrollHideHeader } from 'hooks/use-scroll-hide-header';
import React, { forwardRef, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useThemeContext } from 'contexts';
import prioritizeFeeds from 'utils/prioritize-feeds';
import { sortOptions } from 'utils/select-options';
import ProviderDisclaimer from 'components/provider-disclaimer';
import Breadcrumbs, { type Breadcrumb } from 'components/breadcrumbs';
import styles from './style.module.scss';
import ListingStatsBar from './components/listing-stats-bar';
import MarketFaqAccordion from 'components/pages/market-faq/market-faq-accordion';
import { STATIC_MARKET_OVERVIEW_BY_SLUG } from 'components/pages/city-info/static-market-overview';
import dynamic from 'next/dynamic';
import { InsightsResponse, SingleAddress } from '@zoocasa/go-search';
import AreaGuides from 'components/pages/area-listings/components/guides/area-guides';
import { NamedContents } from 'components/named-content';
import { ListingFilterBar } from 'components/listing-filter-bar';
import { FilterChipBar } from 'components/listing-filter-bar/filter_chip_bar';
import { FilterItemData, getActiveFilters } from 'components/listing-filter-bar/listing_filter_bar';
import generateHeading from 'components/pages/area-listings/util/generate-heading';
import { useOnListingCardClick } from 'utils/listing-card';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'react-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import NextImage from 'next/image';
import {
  CALLOUT_EXPIRED_TYPE,
  CALLOUT_RENTED_TYPE,
  CALLOUT_RENT_TYPE,
  CALLOUT_SALE_TYPE,
  CALLOUT_SOLD_TYPE,
  CalloutType,
  FavouriteDelegate,
} from 'components/search-es/map/ClusterAnnotationCallout';
import { CompliancyDelegateType } from 'components/map-listing-card';

import type { LocalizedLinkData } from 'components/pages/area-listings/generate-dynamic-footer-data';
import type { ListingCardData } from 'components/listing-card';
import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type SearchPrediction from 'data/search-predictions';
import type { PartialDeep } from 'type-fest';
import type { LocalizedSeoLinkData } from 'data/seo-links';
import type { MapPageListing, MapPageCluster } from '@zoocasa/go-search';
import type { DisplayablePin } from 'domain/map-pin/map-pin.types';
import type { Bounds, LatLngPoint } from 'components/search-es/types';
import { ZOOCASA_TENANT_ID, getTenantById, isTenantExpCA } from '@tenants';
import { getTenantFiltersConfig } from '@tenants/utils/configs';
import { AmenitiesFilterPanel } from 'components/search-es/poi/amenities-filter-panel';
import type { PoiCategoryId } from 'data/search/poi/poi-categories';
import { usePoiMapBoundsFetch } from 'hooks/use-poi-map-bounds-fetch';
import { MapBottomDrawer } from 'components/domain/map-bottom-drawer/map-bottom-drawer';
import { DrawerSnapPoint } from 'hooks/use-drawer-gestures';
import { SNAP_DURATION_TWO_STEP } from 'components/atomic/organisms/drawer';
import { HybridPageChatPanel } from 'components/domain/hybrid-page-chat-panel';
import { usePreferencesContext } from 'contexts/preferences';
import { useUserContext } from 'contexts/user';
import { trackSmartchatPageView } from 'utils/smartchat-tracking';
import SparkleIcon from 'components/icon/sparkle-icon';
import ControlButton from 'components/search-es/map/MapControls/ControlButton';
import mapControlStyles from 'components/search-es/map/MapControls/style.module.scss';
import CloseIconExpForm from 'components/icon/close-icon-exp-form';

const SearchFilterDeprecationWarning = dynamic(() => import('components/search-filter-deprecation-warning'), { ssr: false });
const MyLinkMyLead = dynamic(() => import('components/pages/area-listings/components/my-link-my-lead'));
const MyLinkMyLeadCreate = dynamic(() => import('components/my-link-my-lead-create'));
const LocalizedProvinceCitySeoLinks = dynamic(() => import('components/pages/area-listings/components/province-city-seo-links').then(mod => ({ default: mod.LocalizedProvinceCitySeoLinks })));
const MapComponent = dynamic(
  () => import('ui/map-component').then(mod => ({ default: mod.MapComponent })),
  { ssr: false }
);

export interface Pagination {
  page: number;
  size: number;
  count: number;
  total: number;
  /** Sum of listingCount across the building-grouped pins currently rendered. Set only in pin mode. */
  currentListings?: number;
  /** Exact total of matching listings across the entire result set (response.totalListings). Set only in pin mode. */
  totalListings?: number;
}

export type AreaInsightsData = {
  areaData?: InsightsResponse;
  surroundingCities?: SurroundingCities[];
};

type MapComponentMethods = {
  changeLocation(location: LatLngPoint, zoom: number): void;
  clearAnnotations(): void;
};

export interface HybridPageV2ViewProps {
  electedAddress: SingleAddress;
  listingParams: ListingParams;
  listings: ListingCardData[];
  breadcrumbs?: readonly Breadcrumb[];
  pagination: Pagination;
  sort: Sort;
  heading: string;
  /** The area's own info-page slug (e.g. "toronto-on"), used to link/derive market-FAQ content. */
  citySlug?: string;
  // Map props
  clusteredListings?: MapPageCluster[];
  displayablePins?: DisplayablePin[];
  fetchBuildingListings?: (buildingKey: string, signal: AbortSignal) => Promise<MapPageListing[]>;
  isMapLoading?: boolean;
  mapCenterPoint?: LatLngPoint;
  mapZoom?: number;
  compliancyDelegate?: CompliancyDelegateType;
  favouriteDelegate?: FavouriteDelegate;
  onMapBoundingRegionChanged?: (oldBounds: Bounds | null, newBounds: Bounds) => void;
  onMapZoomChanged?: (oldZoom: number, newZoom: number) => void;
  onListingClick?: (listing: MapPageListing) => void;
  onListingAddressClick?: (listing: MapPageListing) => void;
  onTermsRequiredClick?: (listing: MapPageListing) => void;
  onSingleListingClusterClick?: (listing: MapPageListing) => void;
  areaPolygonWkt?: string;
  areaName?: string;
  areaBlurb?: string;
  areaBlurbHeading?: string;
  areaOverviewTitle?: string;
  areaNamedContent?: NamedContents;
  areaInsightsData?: AreaInsightsData;
  showMapCardExperimentView?: boolean;
  internalLinks?: LocalizedLinkData[];
  internalLinksHeading?: string;
  seoLinks?: LocalizedSeoLinkData;
  noListingsInAreaMessage?: string;
  hasNoListingsInArea?: boolean;
  missingAddressMessage?: string;
  expandGuidesByDefault?: boolean;
  showGuides?: boolean;
  showDisclaimer?: boolean;
  showFilterDeprecationWarning?: boolean;
  showNoImageListingsFilterWarning?: boolean;
  showsMyLinkMyLeadCreate?: boolean;
  showsMyLinkMyLead?: boolean;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  /** Numbered-pagination handler — used by pin mode to jump to a specific page. */
  setPageNumber?: (pageNumber: number) => void;
  /** Which pagination UI to render in the listings grid. Defaults to PREVNEXT. */
  paginationMode?: PaginationMode;
  onMobileFilterButtonClick(): void;
  onSortChanged(sort: Sort): void;
  onValueChange: (filters: PartialDeep<Filter>, changedFilter?: string) => void;
  onSaveSearchButtonClick: () => void;
  listingsContainerRef: React.RefObject<HTMLDivElement>;
  onLocationSearchClick: (searchPrediction: SearchPrediction) => void;
  onMapIconClick: () => void;
  onMoreButtonClick: () => void;
  onDeprecationWarningButtonClick?: () => void;
  onNoImageListingsFilterWarningClose?: () => void;
  trackMapCardExperimentView?: () => void;
  trackMapCardClick?: () => void;
  isProvinceOrCountryAddress?: boolean;
  isMobileListView: boolean;
  setIsMobileListView: (isMobileListView: boolean) => void;
  isCrawler?: boolean;
  mapkitSnapshotUrl?: string;
  isMapCenterPointLoading?: boolean;
  isMapCenterPointError?: boolean;
  mapCenterPointError?: Error;
  useLayoutV2?: boolean;
  // Analytics handlers from controller
  onFilterButtonClickAnalytics?: () => void;
  onSortChangedAnalytics?: (sort: Sort) => void;
  onSaveSearchButtonClickAnalytics?: () => void;
  useBuildingGroupedPins?: boolean;
  onVisiblePinIdsChanged?: (ids: Set<number>) => void;
  isListingsLoading?: boolean;
  onSidePanelExpandedChange?: (expanded: boolean) => void;
}

export type HybridPageV2ViewMethods = MapComponentMethods;

const HybridPageV2View = forwardRef(function HybridPageV2View(props: HybridPageV2ViewProps, ref: React.ForwardedRef<HybridPageV2ViewMethods>) {
  const {
    electedAddress,
    listingParams,
    listings,
    pagination: {
      page, count, total, size,
    },
    heading,
    citySlug,
    breadcrumbs,
    listingsContainerRef,
    clusteredListings,
    displayablePins,
    fetchBuildingListings,
    isMapLoading,
    mapCenterPoint,
    mapZoom,
    compliancyDelegate,
    favouriteDelegate,
    onMapBoundingRegionChanged,
    onMapZoomChanged,
    onListingClick,
    onListingAddressClick,
    onTermsRequiredClick,
    onSingleListingClusterClick,
    areaPolygonWkt,
    areaName,
    areaBlurb,
    areaBlurbHeading,
    areaInsightsData,
    areaNamedContent,
    areaOverviewTitle,
    showMapCardExperimentView,
    internalLinks,
    internalLinksHeading,
    seoLinks,
    noListingsInAreaMessage,
    hasNoListingsInArea,
    missingAddressMessage,
    expandGuidesByDefault,
    showGuides,
    showDisclaimer,
    showFilterDeprecationWarning = false,
    showNoImageListingsFilterWarning = false,
    showsMyLinkMyLeadCreate = false,
    showsMyLinkMyLead = false,
    onPrevPage,
    onNextPage,
    setPageNumber,
    paginationMode = PaginationMode.PREVNEXT,
    onSortChanged,
    onMobileFilterButtonClick,
    onValueChange,
    onSaveSearchButtonClick,
    onLocationSearchClick,
    onMapIconClick,
    onMoreButtonClick,
    onDeprecationWarningButtonClick = () => { /* no-op */ },
    onNoImageListingsFilterWarningClose = () => { /* no-op */ },
    trackMapCardExperimentView,
    trackMapCardClick,
    isProvinceOrCountryAddress = false,
    isMobileListView,
    setIsMobileListView,
    isCrawler = false,
    mapkitSnapshotUrl,
    isMapCenterPointLoading,
    isMapCenterPointError,
    mapCenterPointError,
    onFilterButtonClickAnalytics,
    onSortChangedAnalytics,
    onSaveSearchButtonClickAnalytics,
    useBuildingGroupedPins: _useBuildingGroupedPins = false,
    onVisiblePinIdsChanged,
    isListingsLoading = false,
    onSidePanelExpandedChange,
  } = props;
  const onListingCardClick = useOnListingCardClick();
  const isMobile = useIsMobile();
  const { tenantId } = useThemeContext();
  const { siteLocation } = useUserContext();
  const { isAiSearch: _isAiSearch } = usePreferencesContext();
  // Initialize with current filters from props
  const [activeFilterItems, setActiveFilterItems] = useState<FilterItemData[]>(() => getActiveFilters(listingParams.filter));
  const filterBarIsOpen = activeFilterItems.length !== 0;

  // `heading` is baked into props at SSR — it never changes on client-side filter add/remove
  // (this page updates filters without a full page reload). Once any filter is active, recompute
  // the classic filter-aware heading client-side so it tracks the current filter state instead
  // of staying frozen at whatever was true on first load.
  const displayHeading = useMemo(() => {
    if (activeFilterItems.length === 0) {
      return heading;
    }
    return generateHeading({
      areaName: electedAddress.label,
      areaType: electedAddress.addressType,
      filter: listingParams.filter,
      city: electedAddress.subDivision,
      neighbourhood: electedAddress.neighbourhood,
    });
  }, [activeFilterItems.length, heading, electedAddress, listingParams.filter]);

  // SmartChat state
  const tenant = getTenantById(tenantId);
  const aiSearchEnabled = (tenant.features.AiSearch ?? false);

  // POI amenities filter
  const poiCountryFilter = getTenantFiltersConfig(tenant)?.countryCode;
  const poiAmenitiesFilterEnabled = tenant.features.PoiAmenitiesFilter ?? false;
  const marketFaqEnabled = isTenantExpCA(tenantId);
  const [amenitiesFilterPanelOpen, setAmenitiesFilterPanelOpen] = useState(false);
  const [mapBoundingRegion, setMapBoundingRegion] = useState<Bounds | null>(null);
  const [selectedPoiCategoryIds, setSelectedPoiCategories] = useState<ReadonlySet<PoiCategoryId>>(() => new Set());

  const handleMapBoundingRegionChangedForPoi = useCallback(
    (oldBounds: Bounds | null, newBounds: Bounds) => {
      setMapBoundingRegion(newBounds);
      onMapBoundingRegionChanged?.(oldBounds, newBounds);
    },
    [onMapBoundingRegionChanged]
  );

  const poiMapBoundsFetch = usePoiMapBoundsFetch({
    bounds: mapBoundingRegion,
    selectedPoiCategoryIds,
    country: poiCountryFilter,
  });

  const mapPois = useMemo(() => poiMapBoundsFetch.data?.pois ?? [], [poiMapBoundsFetch.data]);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const hasViewedAiSearch = useRef(false);

  // Track AI search page view once when component mounts with AI search enabled
  useEffect(() => {
    if (aiSearchEnabled && chatPanelOpen && !hasViewedAiSearch.current) {
      hasViewedAiSearch.current = true;
      trackSmartchatPageView();
    }
  }, [aiSearchEnabled, chatPanelOpen]);

  // Toggle the Intel chat panel and emit a GTM open-click event whenever
  // the panel transitions from closed → open. Closing is intentionally
  // not tracked here because the panel has its own internal close paths.
  const handleAiSearchButtonClick = useCallback(() => {
    setChatPanelOpen(prev => {
      if (!prev) {
        AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_INTEL_OPEN);
      }
      return !prev;
    });
  }, []);

  // Jiggle animation state
  const MAP_BUTTON_JIGGLE_FLAG = 'map-button-jiggle';
  const isJiggleExperimentActive = useExperiment({ key: MAP_BUTTON_JIGGLE_FLAG, variations: [0, 1], weights: [0.5, 0.5]});
  const isZoocasaTenant = tenantId === ZOOCASA_TENANT_ID;
  const [accumulatedScroll, setAccumulatedScroll] = useState(0);
  const [shouldJiggle, setShouldJiggle] = useState(false);
  const [hasJiggled, setHasJiggled] = useState(false);
  const [clickedBeforeJiggle, setClickedBeforeJiggle] = useState(false);
  const lastScrollY = useRef(0);
  const JIGGLE_SCROLL_THRESHOLD = typeof window !== 'undefined' ? window.innerHeight * 2 : 1000;
  
  
  // Map bottom drawer
  const showMap = (isMobile || !isProvinceOrCountryAddress) && !isCrawler;
  const mapBottomDrawerEnabled = (tenant.features.PropertyPreviewDrawer ?? false) && isMobile && showMap;
  const [drawerState, setDrawerState] = useState<DrawerSnapPoint>(
    mapBottomDrawerEnabled ? DrawerSnapPoint.Partial : DrawerSnapPoint.Minimized
  );

  useEffect(() => {
    if (isProvinceOrCountryAddress && !isMobile) {
      setIsMobileListView(true);
    }
  }, [isProvinceOrCountryAddress, isMobile, setIsMobileListView]);

  // Track if map has been loaded on mobile
  const [hasLoadedMap, setHasLoadedMap] = useState(false);
  const providerIds = showDisclaimer ? prioritizeFeeds([...new Set(listings.map(l => l.providerId))]) : [];
  const subheading = hasNoListingsInArea ? noListingsInAreaMessage : '';
  const listingsGridParams = {
    page: {
      number: page,
      size: size,
    },
  };
  // In pin mode the controller passes an exact totalPages from /api/map/pins
  // (already clamped to MAX_PIN_OFFSET-reachable). In legacy/cluster mode the
  // value is cursor-derived, hence the +1 sentinel.
  const isNumberedPagination = paginationMode === PaginationMode.NUMBERED;
  const listingsGridMeta = {
    totalCount: count,
    totalPages: isNumberedPagination
      ? total
      : (total === 200 || total === 0 ? total : total + 1),
  };

  const sortingOptions = useMemo(() => sortOptions(listingParams.filter.status || AVAILABLE_STATUS), [listingParams.filter.status]);
  const showInternalLinks = internalLinks?.length > 0;
  const showSeoLinks = seoLinks?.links?.length > 0;
  const listingsGridBreadcrumbs = [...(breadcrumbs || [])];

  // Dynamic callout type logic (same as search page)
  const calloutType: CalloutType = useMemo(() => {
    let type: CalloutType = CALLOUT_EXPIRED_TYPE; // Default fallback
    const filters = listingParams.filter;

    if (filters.rental) {
      if (filters.status === NOT_AVAILABLE_SOLD_STATUS) {
        type = CALLOUT_RENTED_TYPE; // "X rented"
      } else if (filters.status === AVAILABLE_STATUS) {
        type = CALLOUT_RENT_TYPE; // "X rent"
      }
    } else if (filters.status === NOT_AVAILABLE_SOLD_STATUS) {
      type = CALLOUT_SOLD_TYPE; // "X sold"
    } else if (filters.status === AVAILABLE_STATUS) {
      type = CALLOUT_SALE_TYPE; // "X sale"
    }

    return type;
  }, [listingParams.filter]);
  const mapRef = useRef<MapComponentMethods | null>(null);
  // Map Refine: expand/hide map controls removed — keep for reference
  // const [isMapExpanded, setIsMapExpanded] = useState(false);
  // const [isSidePanelExpanded, setIsSidePanelExpanded] = useState(false);
  // useEffect(() => {
  //   onSidePanelExpandedChange?.(isSidePanelExpanded);
  // }, [isSidePanelExpanded, onSidePanelExpandedChange]);
  useEffect(() => {
    onSidePanelExpandedChange?.(false);
  }, [onSidePanelExpandedChange]);
  const { t } = useTranslation(Namespaces.COMMON);
  const [highlightedListingId, setHighlightedListingId] = useState<number | null>(null);
  const [highlightedBuildingKey, setHighlightedBuildingKey] = useState<string | null>(null);
  const [selectedPin, setSelectedPin] = useState<DisplayablePin | null>(null);
  const [showMapControls, setShowMapControls] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);

  const { isFilterBarVisible } = useScrollHideHeader({
    enabled: isMobile && isMobileListView,
  });

  // const handleLeftExpandClick = () => {
  //   if (isMapExpanded) {
  //     AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_SHOW_LIST, 'Map Controls');
  //     setIsMapExpanded(false);
  //     setIsSidePanelExpanded(false);
  //     setShowMapControls(true);
  //   } else {
  //     AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_EXPAND_MAP, 'Map Controls');
  //     setIsMapExpanded(true);
  //     setIsSidePanelExpanded(false);
  //     setShowMapControls(true);
  //   }
  // };

  // const handleRightExpandClick = () => {
  //   if (isSidePanelExpanded) {
  //     AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_RESTORE_MAP, 'Map Controls');
  //     setIsMapExpanded(false);
  //     setIsSidePanelExpanded(false);
  //     setShowMapControls(true);
  //     window.scrollTo({
  //       top: 0,
  //     });
  //   } else {
  //     AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_HIDE_MAP, 'Map Controls');
  //     setIsMapExpanded(false);
  //     setIsSidePanelExpanded(true);
  //     setShowMapControls(true);
  //   }
  // };

  const handlePinSelected = useCallback((pin: DisplayablePin) => {
    setSelectedPin(pin);
  }, []);

  useEffect(() => {
    setHighlightedListingId(null);
    setHighlightedBuildingKey(selectedPin?.pin?.buildingKey ?? null);
  }, [selectedPin]);

  useImperativeHandle(ref, () => ({
    changeLocation(location, zoom) {
      if (mapRef?.current?.changeLocation) {
        mapRef?.current?.changeLocation(location, zoom);
      }
    },
    clearAnnotations() {
      if (mapRef?.current?.clearAnnotations) {
        mapRef.current.clearAnnotations();
      }
    },
  }), []);

  const ProviderDisclaimerComponent = React.memo(
    ({ providerIds }: { providerIds: number[] }) => {
      return providerIds.map(p => <ProviderDisclaimer key={p} providerId={p} tenantName={'zoocasa'}/>);
    },
    (prevProps, nextProps) => {
      if (prevProps.providerIds.length !== nextProps.providerIds.length) {
        return false;
      }
      return prevProps.providerIds.every((id, index) => id === nextProps.providerIds[index]);
    }
  );

  useEffect(() => {
    setActiveFilterItems(getActiveFilters(listingParams.filter));
  }, [listingParams.filter]);

  useEffect(() => {
    if (isMobile && isMobileListView) {
      window.scrollTo({
        top: scrollPosition,
      });
    }
    else if (isMobile) {
      window.scrollTo({
        top: 0,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileListView]);

  useEffect(() => {
    if (isMobile && !isMobileListView) {
      document.body.style.overflow = 'hidden';
    } else {
      // Do not set `overflow: auto` — it overrides global `overflow-x: clip` on body
      // (application.scss) and re-enables horizontal scrolling when content is slightly wide.
      document.body.style.removeProperty('overflow');
    }
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [isMobile, isMobileListView]);

  // Track scroll distance for jiggle animation
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollDelta = Math.abs(currentScrollY - lastScrollY.current);
    lastScrollY.current = currentScrollY;

    setAccumulatedScroll(prev => prev + scrollDelta);
  }, []);

  useEffect(() => {
    if (isJiggleExperimentActive.variationId !== 1 || !isZoocasaTenant || !isMobileListView || !isMobile) {
      return;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isJiggleExperimentActive.variationId === 1, isZoocasaTenant, isMobileListView, isMobile, handleScroll]);

  // Trigger jiggle when scroll threshold is reached
  useEffect(() => {
    if (accumulatedScroll >= JIGGLE_SCROLL_THRESHOLD && isJiggleExperimentActive.variationId === 1 && isZoocasaTenant && isMobileListView) {
      setShouldJiggle(true);
      setHasJiggled(true);
    }
  }, [accumulatedScroll, JIGGLE_SCROLL_THRESHOLD, isJiggleExperimentActive.variationId, isZoocasaTenant, isMobileListView]);

  // Reset scroll tracking after jiggle animation completes
  useEffect(() => {
    if (shouldJiggle) {
      const timer = setTimeout(() => {
        setShouldJiggle(false);
        setAccumulatedScroll(0);
      }, 600); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [shouldJiggle]);

  // Mark map as loaded once user views it (desktop or mobile map view)
  useEffect(() => {
    if (!isMobile || !isMobileListView) {
      setHasLoadedMap(true);
    }
  }, [isMobile, isMobileListView]);

  // Scrolls only the listings panel (never the outer page) down to the market FAQ section, so a
  // sticky map isn't left behind. Province/country pages hide the map on desktop and don't give
  // `.area-page-content` a capped height (`full-height` vs. the normal `screen-height`), so the
  // panel never actually overflows — the outer page scrolls instead, making `container.scrollTo()`
  // a no-op. Detect that case and fall back to `scrollIntoView`, which finds whichever ancestor
  // (the panel, or the page) is actually scrollable.
  // Infinite-scroll listing pagination can grow the panel and push the FAQ further down mid-animation,
  // so we re-measure and correct once more after the first scroll settles.
  const scrollListingsToMarketFaq = useCallback(() => {
    const container = listingsContainerRef.current;
    if (!container) return;
    const scrollToFaq = () => {
      const target = document.getElementById('market-faq');
      if (!target) return;
      if (container.scrollHeight <= container.clientHeight) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      container.scrollTo({ top: offset, behavior: 'smooth' });
    };
    scrollToFaq();
    window.setTimeout(scrollToFaq, 600);
  }, [listingsContainerRef]);

  return (
    <div className={styles['hybrid-page-v2']}>
      <div className={styles['breadcrumbs-wrapper']}>
        <Breadcrumbs breadcrumbs={breadcrumbs || []} />
      </div>
      {isMobile && !isCrawler && (
        <div className={buildClassName(
          styles['mobile-filter-bar-fixed'],
          isMobileListView && styles['solid-background'],
          isMobile && isMobileListView && (isFilterBarVisible ? styles['is-visible'] : styles['is-hidden'])
        )}>
          {isMobileListView || showsMyLinkMyLeadCreate ? (
            <>
              <MobileListingFilterBarV2
                sort={listingParams.sort}
                sortingOptions={getLocalizedSortOptions(listingParams.filter.status || AVAILABLE_STATUS, t)}
                onSortChanged={onSortChanged}
                onMobileFilterButtonClick={onMoreButtonClick}
                showsMyLinkMyLeadCreate={showsMyLinkMyLeadCreate}
                showSelectDropdown={isMobileListView}
                hasFiltersSet={filterBarIsOpen}
                onSaveSearchButtonClick={onSaveSearchButtonClick}
                showSaveSearchButton={!isMobileListView}
                onFilterButtonClickAnalytics={onFilterButtonClickAnalytics}
                onSortChangedAnalytics={onSortChangedAnalytics}
                onSaveSearchButtonClickAnalytics={onSaveSearchButtonClickAnalytics}
              />
              {isMobileListView && (
                <h1 className={styles['listing-count']}>
                  {displayHeading}
                </h1>
              )}
            </>
          ) : (
            <div className={styles['mobile-map-buttons']}>
              <IconButton
                leftIcon={<AtomicFilterIcon />}
                label={t('allFilters', { defaultValue: 'All Filters' })}
                onClick={onMoreButtonClick}
              />
              {aiSearchEnabled && (
                <ControlButton
                  handler={handleAiSearchButtonClick}
                  isActive={chatPanelOpen}
                  className={mapControlStyles['compact-button']}
                >
                  {chatPanelOpen ? <CloseIconExpForm /> : <SparkleIcon />}
                </ControlButton>
              )}
            </div>
          )}
        </div>
      )}
      <div className={styles.component}>
        <section className={styles['search-nav']}>
          <ListingFilterBar
            onLocationSearchClick={onLocationSearchClick}
            activeFilterItems={activeFilterItems}
            setActiveFilterItems={setActiveFilterItems}
            onValueChange={onValueChange}
            onSaveSearchButtonClick={onSaveSearchButtonClick}
            onMapIconClick={onMapIconClick}
            onMoreButtonClick={onMoreButtonClick}
            filters={listingParams.filter}
            showNoImageListingsFilterWarning={showNoImageListingsFilterWarning}
            onNoImageListingsFilterWarningClose={onNoImageListingsFilterWarningClose}
            hideMapViewButton={true}
            hideFilterChipBarSaveSearchButton={true}
          />
        </section>
        {showsMyLinkMyLead ?
          <MyLinkMyLead />
          :
          null
        }
        <div
          className={buildClassName(
            styles['content-container'],
            isProvinceOrCountryAddress ? styles['full-height'] : activeFilterItems.length > 0 ? styles['screen-height-bump'] : styles['screen-height'],
            isProvinceOrCountryAddress && styles['province-full-width'],
            poiAmenitiesFilterEnabled && amenitiesFilterPanelOpen && styles['amenities-drawer-open']
          )}
        >
          { !isCrawler &&
            <div className={buildClassName(
              styles['bottom-buttons-mobile'],
              isMobileListView && styles['bottom-buttons-mobile-centered'],
              mapBottomDrawerEnabled && !isMobileListView && styles['drawer-offset'],
              shouldJiggle && styles['jiggle'],
              'mobile-only'
            )}>
              {!isMobileListView && !amenitiesFilterPanelOpen &&
                <Button
                  className={styles['remove-boundary-btn']}
                  theme={SECONDARY_THEME}
                  label={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_REMOVE_BOUNDARY, { defaultValue: 'Remove Boundary' })}
                  onClick={onMapIconClick}
                />
              }
              {(!mapBottomDrawerEnabled || isMobileListView) && showMap && (
                <Button
                  className={buildClassName(styles['map-list-toggle-fixed'], isMobileListView && styles['toggle-secondary'])}
                  label={isMobileListView ? t(LOCALE_COMMON_PATHS.HYBRID_PAGE_MOBILE_V2_TOGGLE_MAP, { defaultValue: 'Map' }) : t(LOCALE_COMMON_PATHS.HYBRID_PAGE_MOBILE_V2_TOGGLE_LIST, { defaultValue: 'List' })}
                  Icon={isMobileListView ? MapIcon : ListViewIcon}
                  onClick={() => {
                    if (hasJiggled) {
                      AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_AFTER_JIGGLE);
                    } else if (!clickedBeforeJiggle) {
                      AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_BEFORE_JIGGLE);
                      setClickedBeforeJiggle(true);
                    }
                    AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE);
                    if (isMobileListView) {
                      setScrollPosition(window.pageYOffset);
                      if (mapBottomDrawerEnabled) {
                        setDrawerState(DrawerSnapPoint.Minimized);
                      }
                    }
                    setIsMobileListView(!isMobileListView);
                  }}
                  testId={searchFilterIds.mapButtonAreaPage}
                />
              )}
            </div>
          }
          <div ref={listingsContainerRef} className={buildClassName(styles['area-page-content'], !isMobileListView && isMobile && styles['side-panel-hidden'])}>
            <div className={styles['title-sort-row']}>
              <div className={styles['title-block']}>
                <h1 className={styles['page-title']} data-testid={headerIds.pageHeading}>
                  {displayHeading}
                </h1>
                {missingAddressMessage && (
                  <h2 className={styles['missing-address']}>
                    {missingAddressMessage}
                  </h2>
                )}
                {subheading && (
                  <h2 className={styles['page-subtitle']}>
                    {subheading}
                  </h2>
                )}
              </div>
              <div className={styles['sub-filter']}>
                <SelectField
                  className={styles['sort-by']}
                  testId={searchFilterIds.sortByDropdown}
                  ariaLabel={'Sort by dropdown'}
                  value={listingParams.sort}
                  options={sortingOptions}
                  onValueChange={onSortChanged}
                />
                <Button
                  className={styles['mobile-filter-btn']}
                  testId={searchFilterIds.mobileFilterButton}
                  theme={TERTIARY_THEME}
                  Icon={FilterIcon}
                  label="Filters"
                  onClick={onMobileFilterButtonClick}
                />
                {showsMyLinkMyLeadCreate && !isMobile ?
                  <MyLinkMyLeadCreate /> :
                  <></>
                }
              </div>
              {(isMobile && filterBarIsOpen) && <div className={styles['mobile-filter-chip-bar']}>
                <FilterChipBar
                  activeFilterItems={activeFilterItems}
                  setActiveFilterItems={setActiveFilterItems}
                  onValueChange={onValueChange}
                  onSaveSearchButtonClick={onSaveSearchButtonClick}
                  filters={listingParams.filter}
                  isMobile={isMobile}
                  hideSaveSearchButton={true}
                />
              </div>}
            </div>
            <ListingStatsBar insightsData={areaInsightsData?.areaData} citySlug={electedAddress.slug} onMoreStatsClick={scrollListingsToMarketFaq} isFullWidth={isProvinceOrCountryAddress} />
            <ListingsGrid
              testId={testIds.listingGrid}
              listings={listings}
              useListingCardWithCompliance={false}
              isLoading={isListingsLoading}
              params={listingsGridParams}
              meta={listingsGridMeta}
              showMapCardExperimentView={showMapCardExperimentView}
              breadcrumbs={listingsGridBreadcrumbs}
              onPrevPage={onPrevPage}
              onNextPage={onNextPage}
              setPageNumber={setPageNumber}
              paginationMode={paginationMode}
              listingParams={listingParams}
              trackMapCardExperimentView={trackMapCardExperimentView}
              trackMapCardClick={trackMapCardClick}
              onListingCardClick={onListingCardClick}
              isOnSidePanel={!isProvinceOrCountryAddress}
              onListingHover={displayablePins && displayablePins.length > 0 ? setHighlightedListingId : undefined}
            />
            {marketFaqEnabled &&
            <MarketFaqAccordion
              areaName={areaName}
              insightsData={areaInsightsData?.areaData}
              nearbyCities={areaInsightsData?.surroundingCities?.map(city => ({ name: city.city, slug: city.slug }))}
              citySlug={citySlug}
              staticSections={citySlug ? STATIC_MARKET_OVERVIEW_BY_SLUG[citySlug] : undefined}
            />}
            {showGuides &&
            <AreaGuides
              areaOverviewTitle={areaOverviewTitle}
              areaName={areaName}
              areaBlurbHeading={areaBlurbHeading}
              areaBlurb={areaBlurb}
              namedContent={areaNamedContent}
              isActive={expandGuidesByDefault}
              insightsData={areaInsightsData?.areaData}
            />
            }
            {showDisclaimer && <ProviderDisclaimerComponent providerIds={providerIds}/>}
            {showInternalLinks && <LocalizedInternalLinks isActive={expandGuidesByDefault} data={internalLinks} heading={internalLinksHeading} />}
            {showSeoLinks && <LocalizedProvinceCitySeoLinks seoLinks={seoLinks} />}

          </div>
          {!isCrawler ? (
            <div className={buildClassName(styles['map-container'], ((isProvinceOrCountryAddress && !isMobile) || (isMobileListView && isMobile)) && styles['map-container-hidden'], mapBottomDrawerEnabled && styles['drawer-active'])}>
              {!isProvinceOrCountryAddress && showMapControls && !isCrawler && (
                <div className={buildClassName(
                  styles['remove-boundary-desktop'],
                  'desktop-only',
                  activeFilterItems.length > 0 && styles['remove-boundary-desktop-bump']
                )}>
                  <Button
                    theme={SECONDARY_THEME}
                    label={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_REMOVE_BOUNDARY, { defaultValue: 'Remove Boundary' })}
                    onClick={onMapIconClick}
                    testId={searchFilterIds.removeBoundaryButton}
                  />
                </div>
              )}
              {hasLoadedMap && (
                <Suspense fallback={<section className={styles['loading-map']} />}>
                  <MapComponent
                    ref={mapRef}
                    onSavedSearchFilterClick={onMoreButtonClick}
                    clusters={clusteredListings}
                    displayablePins={displayablePins}
                    highlightedListingId={highlightedListingId}
                    highlightedBuildingKey={highlightedBuildingKey}
                    onPinSelected={handlePinSelected}
                    fetchBuildingListings={fetchBuildingListings}
                    showsLoading={isMapLoading}
                    showsSidePanel={false}
                    isSidePanelFullScreen={false}
                    center={mapCenterPoint}
                    zoom={mapZoom ?? listingParams.filter.zoom}
                    onMapBoundingRegionChanged={handleMapBoundingRegionChangedForPoi}
                    onMapZoomChanged={onMapZoomChanged}
                    showDebugOverlay={false}
                    calloutType={calloutType}
                    compliancyDelegate={compliancyDelegate as CompliancyDelegateType}
                    favouriteDelegate={favouriteDelegate as FavouriteDelegate}
                    onListingClick={onListingClick}
                    onListingAddressClick={onListingAddressClick}
                    onTermsRequiredClick={onTermsRequiredClick}
                    onSingleListingClusterClick={onSingleListingClusterClick}
                    polygonWkt={isProvinceOrCountryAddress ? undefined : areaPolygonWkt}
                    isAiSearch={false}
                    isHybridPage={true}
                    setShowMapControls={setShowMapControls}
                    isMapCenterPointLoading={isMapCenterPointLoading}
                    isMapCenterPointError={isMapCenterPointError}
                    mapCenterPointError={mapCenterPointError}
                    onVisiblePinIdsChanged={onVisiblePinIdsChanged}
                    mapPois={mapPois}
                    onAmenitiesClick={
                      poiAmenitiesFilterEnabled
                        ? () => setAmenitiesFilterPanelOpen(prev => !prev)
                        : undefined
                    }
                    numAmenitiesSelected={poiAmenitiesFilterEnabled ? selectedPoiCategoryIds.size : 0}
                    amenitiesFilterOpen={poiAmenitiesFilterEnabled && amenitiesFilterPanelOpen}
                    onGuidedClick={aiSearchEnabled ? handleAiSearchButtonClick : undefined}
                    guidedActive={chatPanelOpen}
                  />
                </Suspense>
              )}
              {poiAmenitiesFilterEnabled && (
                <AmenitiesFilterPanel
                  open={amenitiesFilterPanelOpen}
                  onClose={() => setAmenitiesFilterPanelOpen(false)}
                  panelType={isMobile ? 'drawer' : 'popover'}
                  selectedIds={selectedPoiCategoryIds}
                  setSelectedIds={setSelectedPoiCategories}
                />
              )}
            </div>
          ) : (
            <div className={styles['static-map-container']}>
              <NextImage src={mapkitSnapshotUrl} alt={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_MAP, { defaultValue: 'Map' })} width={640} height={480} layout='responsive' />
            </div>
          )}
          {mapBottomDrawerEnabled && !isMobileListView && !amenitiesFilterPanelOpen && (
            <MapBottomDrawer
              listings={listings}
              totalCount={count}
              title={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_DRAWER_HOMES_IN_AREA, { count: formatNumberWithSuffix(count, false), area: electedAddress.label, defaultValue: `${formatNumberWithSuffix(count, false)} Homes in ${electedAddress.label}` })}
              onListingCardClick={onListingCardClick}
              initialState={drawerState}
              zIndex={10}
              topOffset={40}
              onStateChange={newState => {
                setDrawerState(newState);
                if (newState === DrawerSnapPoint.Full) {
                  setTimeout(() => setIsMobileListView(true), SNAP_DURATION_TWO_STEP);
                }
              }}
            />
          )}
          <HybridPageChatPanel
            isOpen={chatPanelOpen}
            onClose={() => setChatPanelOpen(false)}
            filters={listingParams.filter}
            onFiltersChanged={(changedFilters, changedFilter) => {
              onValueChange(changedFilters, changedFilter);
            }}
            onMapLocationChange={location => {
              if (mapRef.current) {
                mapRef.current.changeLocation(location, location.zoom || 12);
              }
            }}
            totalListingsCount={count}
            isMobile={isMobile}
            activeFilterItems={activeFilterItems}
            setActiveFilterItems={setActiveFilterItems}
            country={siteLocation}
          />
        </div>

      </div>

      {showFilterDeprecationWarning &&
        <SearchFilterDeprecationWarning onClose={onDeprecationWarningButtonClick}/>
      }
    </div>);
});

export default HybridPageV2View;
